/**
 * MergeSuggestionBanner Component
 *
 * Displays pending merge suggestions with batch actions and triage mode.
 * Summary bar: Merge All / Reject All / Review buttons.
 * Triage mode: Step through suggestions one at a time with accept/reject/skip.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  GitMerge,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import type { MergeSuggestion } from '../../types';

interface MergeSuggestionBannerProps {
  suggestions: MergeSuggestion[];
  onAccept: (suggestion: MergeSuggestion) => Promise<void>;
  onReject: (suggestion: MergeSuggestion) => Promise<void>;
  onBatchAccept: () => Promise<void>;
  onBatchReject: () => Promise<void>;
  onTriageComplete?: () => void;
}

function SimilarityBadge({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  let color = 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
  if (score < 0.7) color = 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300';
  if (score < 0.5) color = 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';

  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color}`}>
      {pct}% similar
    </span>
  );
}

export function MergeSuggestionBanner({
  suggestions,
  onAccept,
  onReject,
  onBatchAccept,
  onBatchReject,
  onTriageComplete,
}: MergeSuggestionBannerProps) {
  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');

  const [triageMode, setTriageMode] = useState(false);
  const [triageIndex, setTriageIndex] = useState(0);
  const [localSuggestions, setLocalSuggestions] = useState<MergeSuggestion[]>([]);
  const [processing, setProcessing] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState<'accept' | 'reject' | null>(null);
  const [triageStats, setTriageStats] = useState({ accepted: 0, rejected: 0, skipped: 0 });
  const [expanded, setExpanded] = useState(false);

  // Sync local suggestions when parent changes
  useEffect(() => {
    if (!triageMode) {
      setLocalSuggestions(pendingSuggestions);
    }
  }, [suggestions, triageMode]);

  const enterTriageMode = () => {
    setLocalSuggestions([...pendingSuggestions]);
    setTriageIndex(0);
    setTriageStats({ accepted: 0, rejected: 0, skipped: 0 });
    setTriageMode(true);
  };

  const currentSuggestion = triageMode ? localSuggestions[triageIndex] : null;
  const isTriageComplete = triageMode && triageIndex >= localSuggestions.length;

  const handleTriageAccept = useCallback(async () => {
    if (!currentSuggestion || processing) return;
    setProcessing(true);
    try {
      await onAccept(currentSuggestion);
      setTriageStats(prev => ({ ...prev, accepted: prev.accepted + 1 }));
      setLocalSuggestions(prev => prev.filter(s => s.id !== currentSuggestion.id));
      // Don't increment index since we removed an item
    } catch {
      // On failure the item stays in place
    } finally {
      setProcessing(false);
    }
  }, [currentSuggestion, processing, onAccept]);

  const handleTriageReject = useCallback(async () => {
    if (!currentSuggestion || processing) return;
    setProcessing(true);
    try {
      await onReject(currentSuggestion);
      setTriageStats(prev => ({ ...prev, rejected: prev.rejected + 1 }));
      setLocalSuggestions(prev => prev.filter(s => s.id !== currentSuggestion.id));
    } catch {
      // On failure the item stays in place
    } finally {
      setProcessing(false);
    }
  }, [currentSuggestion, processing, onReject]);

  const handleTriageSkip = useCallback(() => {
    if (processing) return;
    setTriageStats(prev => ({ ...prev, skipped: prev.skipped + 1 }));
    setTriageIndex(prev => prev + 1);
  }, [processing]);

  // Keyboard shortcuts in triage mode
  useEffect(() => {
    if (!triageMode || isTriageComplete) return;
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'a') handleTriageAccept();
      if (e.key === 'r') handleTriageReject();
      if (e.key === 'ArrowRight') handleTriageSkip();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [triageMode, isTriageComplete, handleTriageAccept, handleTriageReject, handleTriageSkip]);

  const handleBatchAccept = async () => {
    setBatchProcessing('accept');
    try {
      await onBatchAccept();
    } finally {
      setBatchProcessing(null);
    }
  };

  const handleBatchReject = async () => {
    setBatchProcessing('reject');
    try {
      await onBatchReject();
    } finally {
      setBatchProcessing(null);
    }
  };

  const exitTriage = () => {
    setTriageMode(false);
    onTriageComplete?.();
  };

  if (pendingSuggestions.length === 0 && !triageMode) return null;

  // Triage complete screen
  if (triageMode && isTriageComplete) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle size={18} className="text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-green-900 dark:text-green-200">Triage complete</span>
            <span className="text-xs text-green-700 dark:text-green-300">
              {triageStats.accepted} merged, {triageStats.rejected} rejected, {triageStats.skipped} skipped
            </span>
          </div>
          <button
            onClick={exitTriage}
            className="text-sm text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-300 font-medium"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Triage mode - single suggestion view
  if (triageMode && currentSuggestion) {
    const typeLabel = currentSuggestion.entry_type.charAt(0).toUpperCase() + currentSuggestion.entry_type.slice(1);
    const remaining = localSuggestions.length - triageIndex;

    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <GitMerge size={16} className="text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
              Reviewing {triageIndex + 1} of {localSuggestions.length}
            </span>
            <span className="text-xs text-blue-600 dark:text-blue-400">({remaining} remaining)</span>
          </div>
          <button
            onClick={exitTriage}
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Exit Review
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Possible duplicate {typeLabel.toLowerCase()}
            </span>
            <SimilarityBadge score={currentSuggestion.similarity_score} />
          </div>
          {currentSuggestion.merge_rationale && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{currentSuggestion.merge_rationale}</p>
          )}
          {currentSuggestion.suggested_merged_name && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Suggested name: <span className="font-medium">{currentSuggestion.suggested_merged_name}</span>
            </p>
          )}

          {currentSuggestion.suggested_merged_data && (
            <>
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-2"
              >
                {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                Merged result preview
              </button>
              {expanded && (
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded p-3 text-xs text-gray-700 dark:text-gray-300 mb-3">
                  <pre className="whitespace-pre-wrap">
                    {JSON.stringify(currentSuggestion.suggested_merged_data, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleTriageAccept}
              disabled={processing}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
            >
              <Check size={14} />
              Accept
              <span className="text-xs opacity-70 ml-1">(a)</span>
            </button>
            <button
              onClick={handleTriageReject}
              disabled={processing}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
            >
              <X size={14} />
              Reject
              <span className="text-xs opacity-70 ml-1">(r)</span>
            </button>
            <button
              onClick={handleTriageSkip}
              disabled={processing}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
            >
              Skip
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Summary bar (default mode)
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle size={16} className="text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
            {pendingSuggestions.length} pending merge suggestion{pendingSuggestions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleBatchAccept}
            disabled={batchProcessing !== null}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            <GitMerge size={14} />
            {batchProcessing === 'accept' ? 'Merging...' : 'Merge All'}
          </button>
          <button
            onClick={handleBatchReject}
            disabled={batchProcessing !== null}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            <X size={14} />
            {batchProcessing === 'reject' ? 'Rejecting...' : 'Reject All'}
          </button>
          <button
            onClick={enterTriageMode}
            disabled={batchProcessing !== null}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/40 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            Review
          </button>
        </div>
      </div>
    </div>
  );
}
